import { useEffect, useState } from 'react';
import moment from 'moment';

import Table from '../../../shared/table';
import Input from '../../../shared/input';
import Button from '../../../shared/button';
import Loading from '../../../shared/Loading';
import DateRangePopover from '../../../components/shared/date/DateRange';
import { IPaginationPayload } from '../../../interfaces/shared/IPaginationPayload';
import {
    MarketingTableRow,
    useLazyGetMarketingReportsQuery,
} from '../../../store/marketing/marketingApi';

interface MarketingTableSectionProps {
    title: string;
    type: 'report' | 'demo';
}

type DateRangeValue = [Date, Date] | null;

const toCsvValue = (value: string | number | null | undefined): string => {
    if (value === null || value === undefined) return '';
    const str = String(value);
    if (/[",\n]/.test(str)) {
        return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
};

const MarketingTableSection = ({
    title,
    type,
}: MarketingTableSectionProps) => {
    const [search, setSearch] = useState<string>('');
    const [datePreset, setDatePreset] = useState<
        | 'none'
        | 'all'
        | 'today'
        | 'yesterday'
        | 'last_7_days'
        | 'last_30_days'
        | 'custom'
    >('none');
    const [dateValue, setDateValue] = useState<DateRangeValue>(null);
    const [paginationState, setPaginationState] = useState<IPaginationPayload>({
        pageNumber: 1,
        pageSize: 10,
    });
    const [hasSubmitted, setHasSubmitted] = useState(true);
    const [isExporting, setIsExporting] = useState(false);
    const [appliedSearch, setAppliedSearch] = useState<string | undefined>(
        undefined,
    );
    const [appliedStartDate, setAppliedStartDate] = useState<
        string | undefined
    >(undefined);
    const [appliedEndDate, setAppliedEndDate] = useState<string | undefined>(
        undefined,
    );

    const handleClear = () => {
        setSearch('');
        setDatePreset('none');
        setDateValue(null);

        setAppliedSearch(undefined);
        setAppliedStartDate(undefined);
        setAppliedEndDate(undefined);

        setPaginationState((prev) => ({
            ...prev,
            pageNumber: 1,
        }));

        setHasSubmitted(true);
    };

    const handleExportCsv = async () => {
        const previousPageNumber = paginationState.pageNumber || 1;
        const previousPageSize = paginationState.pageSize || 10;

        try {
            setIsExporting(true);
            const queryString = new URLSearchParams({
                type,
                ...(appliedSearch ? { search: appliedSearch } : {}),
                page: '1',
                flag: 'true',
                ...(appliedStartDate ? { startDate: appliedStartDate } : {}),
                ...(appliedEndDate ? { endDate: appliedEndDate } : {}),
            }).toString();
            const exportResult = await fetch(
                `${process.env.REACT_APP_USERS_API_URL}/demo-requests/reports?${queryString}`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem(
                            'accessToken',
                        )}`,
                        'ngrok-skip-browser-warning': 'true',
                    },
                },
            ).then((res) => res.json());
            const rawElements = exportResult?.data ?? exportResult;
            const exportRows: MarketingTableRow[] = rawElements.map(
                (el: any): MarketingTableRow => ({
                    id: el._id,
                    email: el.businessEmail,
                    fullName: [el.firstName, el.lastName]
                        .filter(Boolean)
                        .join(' '),
                    company: el.hotelName,
                    reportDownloaded: el.type,
                    reportName: el.reportName,
                    createdAt: el.createdAt,
                }),
            );

            if (!exportRows || exportRows.length === 0) {
                return;
            }

            const header = [
                'Email',
                'Full Name',
                'Company/Hotel',
                'Report Downloaded',
				'Report Name2',
                'Created',
            ];

            const dataLines = exportRows.map((row) =>
                [
                    toCsvValue(row.email),
                    toCsvValue(row.fullName),
                    toCsvValue(row.company),
                    toCsvValue(row.reportDownloaded),
                    toCsvValue(row.reportName),
                    toCsvValue(
                        moment(row.createdAt).format('YYYY-MM-DD HH:mm:ss'),
                    ),
                ].join(','),
            );

            const csvContent = [header.join(','), ...dataLines].join('\n');
            const blob = new Blob([csvContent], {
                type: 'text/csv;charset=utf-8;',
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const filePrefix =
                type === 'report' ? 'report-downloads' : 'demo-requests';
            link.download = `${filePrefix}-${moment().format(
                'YYYYMMDD-HHmmss',
            )}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } finally {
            // Restore the previous pagination after export (refetch current page)
            setPaginationState((prev) => ({
                ...prev,
                pageNumber: previousPageNumber,
                pageSize: previousPageSize,
            }));
            setHasSubmitted(true);
			setIsExporting(false);
        }
    };

    const handleShow = () => {
        const startDateParam =
            dateValue && dateValue[0]
                ? moment(dateValue[0]).startOf('day').toISOString()
                : undefined;
        const endDateParam =
            dateValue && dateValue[1]
                ? moment(dateValue[1]).endOf('day').toISOString()
                : undefined;

        setAppliedSearch(search || undefined);
        setAppliedStartDate(startDateParam);
        setAppliedEndDate(endDateParam);
        setHasSubmitted(true);
        setPaginationState((prev) => ({
            ...prev,
            pageNumber: 1,
        }));

        // For debugging / inspection – intentionally left as console output
        // eslint-disable-next-line no-console
        console.log('Marketing table filters', {
            section: title,
            search,
            datePreset,
            startDate: startDateParam || null,
            endDate: endDateParam || null,
        });
    };

    const [fetchReports, { data: apiData, isLoading }] =
        useLazyGetMarketingReportsQuery();

    const rows: MarketingTableRow[] = apiData?.elements || [];
    const paginationData = apiData?.page;

    useEffect(() => {
        if (datePreset === 'custom') {
            return;
        }

        const now = moment();
        let from: moment.Moment | null = null;
        let to: moment.Moment | null = null;

        switch (datePreset) {
            case 'none':
                from = null;
                to = null;
                break;
            case 'all':
                from = moment('1990-01-01').startOf('day');
                to = now.endOf('day');
                break;
            case 'today':
                from = moment().startOf('day');
                to = moment().endOf('day');
                break;
            case 'yesterday':
                from = moment().subtract(1, 'day').startOf('day');
                to = moment().subtract(1, 'day').endOf('day');
                break;
            case 'last_7_days':
                from = moment().subtract(7, 'days').startOf('day');
                to = now.endOf('day');
                break;
            case 'last_30_days':
                from = moment().subtract(30, 'days').startOf('day');
                to = now.endOf('day');
                break;
            default:
                break;
        }

        if (from && to) {
            setDateValue([from.toDate(), to.toDate()]);
        } else {
            setDateValue(null);
        }
    }, [datePreset]);

    useEffect(() => {
        if (!hasSubmitted) return;

        fetchReports({
            search: appliedSearch,
            type,
            pageNumber: paginationState.pageNumber,
            pageSize: paginationState.pageSize,
            startDate: appliedStartDate,
            endDate: appliedEndDate,
        });
    }, [
        hasSubmitted,
        appliedSearch,
        appliedStartDate,
        appliedEndDate,
        paginationState.pageNumber,
        paginationState.pageSize,
        type,
        fetchReports,
    ]);

    return (
        <section className="mt-2">
			<h2 className="text-lg md:text-xl font-semibold text-gray-900 dark:text-white mb-4">
				{title}
			</h2>
            <div className="border border-gray-100 rounded-2xl p-5 bg-white dark:bg-black">
				<div className='border border-gray-100 rounded-2xl'>
					<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
					</div>

                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-black rounded-t-2xl">
                        <div className="flex items-center gap-2">
                            <img
                                src="/assets/shared/filter-icon.svg"
                                alt="filters"
                                className="w-4 h-4"
                            />
                            <p className="text-sm font-bold text-gray-900 dark:text-white">
                                Apply Filters
                            </p>
                        </div>
                    </div>

                    <div className="px-4 py-4 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <label
                                    htmlFor={`${title}-date-range`}
                                    className="block mb-1 text-xs font-medium text-gray-500 dark:text-gray-300"
                                >
                                    Created at
                                </label>
                                <div className="relative">
                                    <select
                                        id={`${title}-date-range`}
                                        value={datePreset}
                                        onChange={(e) =>
                                            setDatePreset(
                                                e.target.value as
                                                    | 'none'
                                                    | 'all'
                                                    | 'today'
                                                    | 'yesterday'
                                                    | 'last_7_days'
                                                    | 'last_30_days'
                                                    | 'custom',
                                            )
                                        }
                                        className="w-full rounded-md border border-[#AFAFAF] dark:border-gray-700 px-3 py-2 pr-9 text-sm outline-none bg-white text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-black dark:text-white appearance-none"
                                    >
                                        <option value="none">Select range</option>
                                        <option value="all">Beginning - Now</option>
                                        <option value="today">Today</option>
                                        <option value="yesterday">Yesterday</option>
                                        <option value="last_7_days">
                                            Last 7 days
                                        </option>
                                        <option value="last_30_days">
                                            Last 30 days
                                        </option>
                                        <option value="custom">Custom</option>
                                    </select>
                                    <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                                        <img
                                            src="/assets/table-actions/arrow.svg"
                                            alt="open"
                                            className="w-2 h-2 rotate-90 dark:invert"
                                        />
                                    </div>
                                </div>

                                {datePreset === 'custom' && (
                                    <div className="mt-2">
                                        <DateRangePopover
                                            label="Custom range"
                                            labelStyle="sr-only"
                                            value={dateValue}
                                            onChange={setDateValue}
                                        />
                                    </div>
                                )}
                            </div>

                            <div>
                                <label
                                    htmlFor={`${title}-search`}
                                    className="block mb-1 text-xs font-medium text-gray-500 dark:text-gray-300"
                                >
                                    Search someone
                                </label>
                                <Input
								    inputStyle='text-sm border-[#AFAFAF]'
                                    inputUniqueName={`${title}-search`}
                                    placeholder="Please enter a hotel name or email to start searching"
                                    inputValue={search}
                                    showValue
                                    onChangeInput={(value) =>
                                        setSearch(value || '')
                                    }
                                />
                            </div>
                        </div>

                        <div className="flex flex-col md:flex-row md:items-center md:justify-end gap-3">
                            <button
                                type="button"
                                onClick={handleClear}
                                className="text-xs text-[#1E293B] self-start md:self-auto bg-[#F1F5F9] px-4 py-2 rounded-md flex items-center gap-1"
                            >
								<img src="/assets/shared/clear-all.svg" alt="clear all" className="w-4 h-4" />
                                <span>Clear All</span>
                            </button>
                            <button
                                type="button"
                                onClick={handleExportCsv}
                                disabled={isExporting}
                                className="text-xs flex items-center gap-2 self-start md:self-auto border border-[#CBD5E1] px-4 py-2 rounded-md text-[#0F172A] bg-white dark:bg-black dark:text-white disabled:opacity-60"
                            >
                                {isExporting ? (
                                    <>
                                        <Loading />
                                        <span>Exporting…</span>
                                    </>
                                ) : (
                                    <>
                                        <img
                                            src="/assets/chat/export.svg"
                                            alt="export csv"
                                            className="w-4 h-4 contrast-0"
                                        />
                                        <span>Export CSV</span>
                                    </>
                                )}
                            </button>
                            <Button
                                label="Show"
                                className="btn-primary-rounded-less !min-w-[120px] text-[13px] !py-1.5"
                                onClickButton={handleShow}
                            />
                        </div>
                    </div>
				</div>

                <Table
                    columns={[
                        'Email',
                        'Full Name',
                        'Company/Hotel',
                        'Report Downloaded',
                        'Report Name',
                        'Created',
                    ]}
                    containerStyle="mt-6 border border-gray-100 rounded-2xl"
                    headerColsStyle='!text-gray-700 dark:text-gray-300 !font-normal !text-sm'
                    paginationData={paginationData}
                    paginationState={paginationState}
                    setPaginationState={setPaginationState}
                >
                    {isLoading && (
                        <tr>
                            <td
                                colSpan={5}
                                className="px-6 py-6 text-center text-sm text-gray-900 dark:text-gray-300"
                            >
                                <div className="flex items-center justify-center gap-2">
                                    <Loading />
                                    <span>Loading data…</span>
                                </div>
                            </td>
                        </tr>
                    )}

                    {!isLoading && rows.length === 0 && (
                        <tr>
                            <td
                                colSpan={5}
                                className="px-6 py-6 text-center text-sm text-gray-500 dark:text-gray-300"
                            >
                                No records found.
                            </td>
                        </tr>
                    )}

                    {!isLoading &&
                        rows.map((row) => (
                            <tr
                                key={row.id}
                                className="bg-white dark:bg-black border-b hover:bg-gray-50 dark:hover:bg-gray-900 text-gray-900 dark:text-white text-xs"
                            >
                                <td className="px-6 py-4">{row.email}</td>
                                <td className="px-6 py-4">{row.fullName}</td>
                                <td className="px-6 py-4">{row.company}</td>
                                <td className="px-6 py-4">
                                    {row.reportDownloaded}
                                </td>
                                <td className="px-6 py-4">
                                    {row.reportName}
                                </td>
                                <td className="px-6 py-4">
                                    {moment(row.createdAt).format(
                                        'D MMM YYYY h:mm a',
                                    )}
                                </td>
                            </tr>
                        ))}
                </Table>
				</div>
        </section>
    );
};

const MarketingView = () => {
    return (
        <div className="dark:text-white">

            <MarketingTableSection
                title="Report Downloads"
                type="report"
            />

            <MarketingTableSection
                title="Demo Requests"
                type="demo"
            />
        </div>
    );
};

export default MarketingView;

